import { intercomBoot, intercomShutdown, intercomShowMessenger } from './intercomService';

import getClientConfig from '../utils/getClientConfig';
import { initialiseRollbar, setRollbarUser } from './rollbarService';
import { isCurrentBrowserLogoutAction } from '../auth/authUtils';
import { isInstalledPwa } from '../pwaApp/pwaUtils';
import { clearAmplitudeUser } from './amplitudeService';
import { getEnabledExperiments } from '../../common/users/utils/userPropertyUtils';
import { getIsElectron } from '../hybridApp/hybridAppUtils';
import { getTimestamp, TIMES } from '../../common/utils/timeUtil';
import { setNewRelicUserId } from './newRelicUtils';

import analyticsEventMiddleware from './analyticsEventMiddleware';
import amplitudeAnalyticsMiddleware from './amplitudeAnalyticsMiddleware';

// Constants
import { INTERCOM_SHOW_MESSAGES } from './analyticsConstants';
import { CURRENT_USER_SET, CURRENT_USER_SET_GUEST } from '../user/currentUserConstants';

export default () => {
    const config = getClientConfig();

    // if no analytics enabled, return empty middleware
    if (!config.analytics || !config.analytics.enabled) {
        return (store) => (next) => (action) => next(action);
    }

    // send user data to intercom
    const sendIntercomUserData = (user) => {
        const createdDate = parseInt(user._id.slice(0, 8), 16);

        const intercomUser = {
            user_id: user._id,
            created_at: createdDate,
            email: user.email,
            name: user.name && user.name.displayName,
            res_width: window.screen.availWidth,
            res_height: window.screen.availHeight,
            hide_default_launcher: true,
            experiments: getEnabledExperiments(user),
        };

        if (getIsElectron()) {
            intercomUser['last-seen-on-electron'] = Math.floor(getTimestamp() / TIMES.SECOND);
        }

        if (isInstalledPwa()) {
            intercomUser['has-used-PWA'] = true;
        }

        intercomBoot({
            app_id: config.analytics.appId,
            ...intercomUser,
        });
    };

    const shutdownIntercom = () => {
        intercomShutdown();
    };

    // return analytics middleware
    return (store) => (next) => {
        const eventMiddleware = analyticsEventMiddleware(store)(next);
        const amplitudeMiddleware = amplitudeAnalyticsMiddleware(store)(next);

        initialiseRollbar();

        return (action) => {
            const updated = next(action);

            eventMiddleware(action);

            // Shut down intercom when user logs out
            if (isCurrentBrowserLogoutAction(action)) {
                clearAmplitudeUser();
                shutdownIntercom();
                return updated;
            }

            if (action.remote) return updated;

            amplitudeMiddleware(action);

            switch (action.type) {
                case CURRENT_USER_SET:
                    // when we set user data, send it to analytics services
                    sendIntercomUserData(action.user);
                    setRollbarUser(action.user);
                    setNewRelicUserId(action.user?._id);
                    break;
                case CURRENT_USER_SET_GUEST:
                    // if we only have a guest user, boot interom without user
                    intercomBoot({
                        app_id: config.analytics.appId,
                        hide_default_launcher: true,
                    });
                    break;
                case INTERCOM_SHOW_MESSAGES:
                    intercomShowMessenger();
                    break;
                default:
                    break;
            }

            return updated;
        };
    };
};
